// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook module for fetching a single fulfillment order by key.
 * Provides a convenient interface for fetching fulfillment order details.
 */
import { getFulfillment } from '@/fulfillment/api/get-fulfillment';
import { fulfillmentDetailQueryKey } from '@/fulfillment/query-keys';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * React hook for fetching a single fulfillment order by key.
 * Uses React Query's suspense mode for data fetching.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Suspense mode for loading states
 * - Automatic refetch on window focus
 *
 * @param fulfillmentKey - The unique key of the fulfillment order to fetch
 *
 * @returns Object containing:
 * - fulfillment: The fulfillment order data
 * - Query status information (error, refetch, etc.)
 *
 * @throws Will suspend while loading data
 * @throws Will throw errors from the API if request fails
 */
export function useFulfillment(fulfillmentKey: string) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: fulfillmentDetailQueryKey(fulfillmentKey),
    queryFn: () => getFulfillment(fulfillmentKey),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  return { fulfillment: data, ...query };
}
